import { type RefObject, useLayoutEffect } from "react";

const MIN_PX = 8;
const DEFAULT_MAX_REM = 0.9375;
const WIDTH_EPSILON = 0.5;
/** Borders / subpixel flex on the bar — 1px “overflow” is noise, not a clip. */
const NAV_WIDTH_EPSILON = 2;
const SEARCH_STEPS = 16;

interface FitOptions {
  cssVar?: string;
  maxRem?: number;
  enabled?: boolean;
  /** Extra boxes that must also fit (e.g. a chapter cell with the Coming soon sizer). */
  alsoMeasureRef?: RefObject<HTMLElement | null>;
}

/**
 * Largest font size at which labels fit, written as a CSS var on `nav`.
 *
 * Past-lockup cells are content-sized, so a label’s own scrollWidth never
 * exceeds its clientWidth — the bar just grows past the viewport and clips
 * “THE VERGE”. Always include the nav’s own overflow in the check.
 */
export function useFitNavLabelFont(
  navRef: RefObject<HTMLElement | null>,
  measureRef: RefObject<HTMLElement | null>,
  layoutKey: boolean,
  {
    cssVar = "--package-nav-label-size",
    maxRem = DEFAULT_MAX_REM,
    enabled = true,
    alsoMeasureRef,
  }: FitOptions = {},
): void {
  useLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!enabled || !nav || !measure) {
      return;
    }

    let cancelled = false;
    let lastWidth = -1;
    const rootPx = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );
    const maxPx =
      maxRem * (Number.isFinite(rootPx) && rootPx > 0 ? rootPx : 16);

    const boxes = (): HTMLElement[] => {
      const extra = alsoMeasureRef?.current;
      return extra && extra !== measure ? [measure, extra] : [measure];
    };

    const overflows = (px: number): boolean => {
      nav.style.setProperty(cssVar, `${px}px`);
      if (nav.scrollWidth - nav.clientWidth > NAV_WIDTH_EPSILON) {
        return true;
      }
      return boxes().some(
        (el) => el.scrollWidth - el.clientWidth > WIDTH_EPSILON,
      );
    };

    const fit = () => {
      if (cancelled) {
        return;
      }
      if (!overflows(maxPx)) {
        nav.style.setProperty(cssVar, `${maxPx}px`);
        return;
      }
      let lo = MIN_PX;
      let hi = maxPx;
      for (let i = 0; i < SEARCH_STEPS; i += 1) {
        const mid = (lo + hi) / 2;
        if (overflows(mid)) {
          hi = mid;
        } else {
          lo = mid;
        }
      }
      nav.style.setProperty(cssVar, `${overflows(lo) ? hi : lo}px`);
    };

    const ro = new ResizeObserver(() => {
      const width = nav.clientWidth;
      if (Math.abs(width - lastWidth) < WIDTH_EPSILON) {
        return;
      }
      lastWidth = width;
      fit();
    });
    ro.observe(nav);
    for (const el of boxes()) {
      ro.observe(el);
    }
    fit();
    void document.fonts.ready.then(fit);

    return () => {
      cancelled = true;
      ro.disconnect();
      nav.style.removeProperty(cssVar);
    };
  }, [navRef, measureRef, alsoMeasureRef, layoutKey, cssVar, maxRem, enabled]);
}
